import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { margin } from "polished";
import React, { PropsWithChildren } from "react";

import type {
  RebirthButtonProps,
  ButtonModifiers,
} from "@brexhq/next/src/components/shared/Button/types";
import type {
  ButtonMargins,
  ResponsiveButtonSizes,
} from "@brexhq/next/src/components/shared/Link/types";
import { createButtonStyles } from "@brexhq/next/src/components/shared/styles/Button.elements";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import { ButtonClickedEvent } from "@brexhq/next/src/telemetry/analyticsEvents";
import { Icon } from "@brexhq/shared/components/deprecated/Icons";
import type { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";

const IconWrapper = styled(Icon)(() => ({
  ...margin(0, 8, 0, 0),
}));

const ModuleThemedIcon = styled(Icon)(({ theme }) => {
  return {
    ...margin(0, 8, 0, 0),
    height: 20,
    width: 20,
    [theme.breakpoints.MED]: {
      height: 18,
      width: 18,
    },
    [theme.breakpoints.SML]: {
      height: 15,
      width: 15,
    },
  };
});

const StyledRebirthButton = styled.button<{
  modifier: ButtonModifiers;
  margin?: ButtonMargins;
  size?: ResponsiveButtonSizes;
  disabled?: boolean;
  moduleTheme?: ModuleThemeType;
}>(({ modifier, margin, size, moduleTheme, disabled }) =>
  createButtonStyles({ modifier, margin, size, theme: moduleTheme, disabled }),
);

export const Button: React.FC<PropsWithChildren<RebirthButtonProps>> = ({
  component,
  label,
  section = "",
  icon,
  iconSize = "lrg",
  modifier = "primary",
  margin,
  onClick,
  testId = "rebirth-button",
  type = "button",
  theme,
  size,
  children,
  disabled,
  role,
  className,
}) => {
  const { trackEvent } = useAnalytics();
  const buttonClicked = () => {
    onClick();
    trackEvent(
      new ButtonClickedEvent(
        component,
        section,
        modifier,
        typeof label === "string" ? label : "",
      ),
    );
  };

  const { colors } = useTheme();

  const iconColor = colors.content.inverse.primary;

  return (
    <StyledRebirthButton
      margin={margin}
      onClick={buttonClicked}
      data-testid={testId}
      modifier={modifier}
      type={type}
      size={size}
      disabled={disabled}
      moduleTheme={theme}
      role={role}
      className={className}
    >
      {icon && !theme && (
        <IconWrapper
          icon={icon}
          color={iconColor}
          height={iconSize == "lrg" ? 24 : 12}
          width={iconSize == "lrg" ? 24 : 12}
        />
      )}
      {icon && theme && <ModuleThemedIcon icon={icon} color={iconColor} />}
      {children ? children : label}
    </StyledRebirthButton>
  );
};
